import DateTimeOperations from '@modules/husleie-framework/src/static-classes/DateTimeOperations.class';
import { Contract } from '@modules/husleie-framework/src/types/Contract.entity';
import {
  RentalAdjustmentInterface,
  RentalAdjustmentTypeEnum,
  Tier,
} from '@modules/husleie-framework/src/types/ContractRentalAdjustment.entity';
import { Deposit, DepositSolution } from '@modules/husleie-framework/src/types/Deposit.entity';
import { PreviousTenant } from '@modules/husleie-framework/src/types/PreviousTenant.entity';
import { Rent } from '@modules/husleie-framework/src/types/Rent.entity';
import type RentalCalculation from '@modules/husleie-framework/src/types/RentalCalculation.entity';
import { Tenant } from '@modules/husleie-framework/src/types/Tenant.entity';
import { APIProtocolVersions } from '@modules/store/common/types/APIProtocolVersions.entity';
import LandlordConversionOperations from '@modules/store/landlord/static-classes/LandlordConversionOperations';
import RentalObjectConversionOperations from '@modules/store/rentalObject/static-classes/RentalObjectConversionOperations';
import TenantConversionOperations from '@modules/store/tenant/static-classes/TenantConversionOperations';
import { UserTranslationOptions } from '@modules/store/user/types/UserTranslationOptions.entity';

export default class ContractConversionOperations {
  static apiToInternal(apiContract: any, options: UserTranslationOptions, versions?: APIProtocolVersions): Contract {
    return this.convertAPIBodyToContract(apiContract, options, versions);
  }

  static internalToApi(contract: Contract, versions?: APIProtocolVersions): any {
    return this.convertContractToAPI(contract, versions);
  }

  private static convertContractToAPI(contract: Contract, versions?: APIProtocolVersions): any {
    return {
      landlord: LandlordConversionOperations.internalToApi(contract.landlord),
      tenant: TenantConversionOperations.internalToApi(contract.tenant),
      coTenants: this.convertCoTenantsToAPI(contract.coTenants),
      amountOfResidents: contract.amountOfResidents,
      deposit: contract.deposit,
      previousTenant: this.convertPreviousTenantToApi(contract.previousTenant),
      rent: this.convertRentToApi(contract.rent),
      rentalAdjustment: this.convertRentalAdjustmentToApi(contract.rentalAdjustment),
      rentalCalculation: this.convertRentalCalculationToApi(contract.rentalCalculation),
      rentalObject: RentalObjectConversionOperations.internalToApi(contract.rentalObject, versions),
      mode: contract.mode,
      currentUpsellPlan: contract.currentUpsellPlan,
    };
  }

  private static convertAPIBodyToContract(
    apiContract: any,
    options: UserTranslationOptions,
    versions?: APIProtocolVersions
  ): Contract {
    return {
      uuid: apiContract.uuid || apiContract.id,
      status: apiContract.status,
      created: DateTimeOperations.apiDateToInternalDate(apiContract.created),
      modified: DateTimeOperations.apiDateToInternalDate(apiContract.modified),
      landlord: LandlordConversionOperations.apiToInternal(apiContract.landlord, options),
      tenant: TenantConversionOperations.apiToInternal(apiContract.tenant, options),
      coTenants: this.convertAPICoTenantsToInternal(apiContract.coTenants, options),
      amountOfResidents: apiContract.amountOfResidents,
      deposit: this.convertAPIDepositToInternal(apiContract.deposit),
      previousTenant: this.convertAPIPreviousToInternal(apiContract.previousTenant),
      rent: this.convertAPIRentToInternal(apiContract.rent),
      rentalAdjustment: this.convertAPIRentalAdjustmentToInternal(apiContract.rentalAdjustment),
      rentalCalculation: this.convertAPIRentalCalculationToInternal(apiContract.rentalCalculation),
      rentalObject: RentalObjectConversionOperations.apiToInternal(apiContract.rentalObject, versions),
      tenancyId: apiContract.tenancyId,
      pdfLink: apiContract.pdfLink,
      mode: apiContract.mode,
      currentUpsellPlan: apiContract.currentUpsellPlan,
    };
  }

  private static convertCoTenantsToAPI(coTenants: Tenant[]): any[] {
    const apiCoTenants: any[] = [];
    coTenants.map((cot: Tenant) => {
      apiCoTenants.push(TenantConversionOperations.internalToApi(cot));
    });
    return apiCoTenants;
  }

  private static convertAPICoTenantsToInternal(apiCoTenants: any[], options: UserTranslationOptions): Tenant[] {
    const coTenants: Tenant[] = [];
    if (apiCoTenants && apiCoTenants.length) {
      apiCoTenants.map((cot: any) => {
        coTenants.push(TenantConversionOperations.apiToInternal(cot, options));
      });
    }
    return coTenants;
  }

  /**
   * Store Tier -> API TIER
   *
   * @param rentalAdjustment
   * @returns
   */
  private static convertRentalAdjustmentToApi(rentalAdjustment: RentalAdjustmentInterface): any {
    const apiTiers: any[] = [];

    rentalAdjustment.tiers.map((tier: Tier) => {
      apiTiers.push({
        date: DateTimeOperations.internalToApiDate(tier.date),
        rent: tier.rent,
      });
    });

    return {
      rentalAdjustment: rentalAdjustment.rentalAdjustment,
      rentRegulation: rentalAdjustment.rentRegulation,
      firstUsedAndRented: rentalAdjustment.firstUsedAndRented,
      renovationsLastThreeYears: rentalAdjustment.renovationsLastThreeYears,
      occupancyAfterRenovations: rentalAdjustment.occupancyAfterRenovations,
      tiers: apiTiers,
    };
  }

  /**
   * convertRentalCalculationToApi
   *
   * @param rentalCalculation
   * @returns
   */
  private static convertRentalCalculationToApi(rentalCalculation: RentalCalculation): any {
    return {
      additionalCost: rentalCalculation.additionalCost,
      additionalCostTypes: rentalCalculation.additionalCostTypes,
      totalRent: rentalCalculation.totalRent,
      utilityCost: rentalCalculation.utilityCost,
      utilityCostBilling: rentalCalculation.utilityCostBilling,
      utilityCostBillingDateFrom: rentalCalculation.utilityCostBillingDateFrom,
      utilityCostBillingDateTo: rentalCalculation.utilityCostBillingDateTo,
      utilityItems: rentalCalculation.utilityItems,
      heatingCost: rentalCalculation.heatingCost,
      heatingCostBilling: rentalCalculation.heatingCostBilling,
      heatingCostBillingDateFrom: rentalCalculation.heatingCostBillingDateFrom,
      heatingCostBillingDateTo: rentalCalculation.heatingCostBillingDateTo,
    };
  }

  private static convertPreviousTenantToApi(previousTenant: undefined | null | PreviousTenant): null | any {
    const returnValue: any = { available: false };
    if (previousTenant) {
      returnValue.available = previousTenant.available;
      if (previousTenant.available) {
        returnValue.lastPaymentDate = DateTimeOperations.internalToApiDate(previousTenant.lastPaymentDate);
        returnValue.lastRent = previousTenant.lastRent;
      } else {
        returnValue.lastPaymentDate = null;
        returnValue.lastRent = null;
      }
    }
    return returnValue;
  }

  private static convertRentToApi(rent: Rent) {
    return {
      bindingPeriod: rent.bindingPeriod,
      durationType: rent.durationType,
      noticePeriod: rent.noticePeriod,
      limitedRentDurationType: rent.limitedRentDurationType,
      limitedRentReason: rent.limitedRentReason,
      startDate: DateTimeOperations.internalToApiDate(rent.startDate),
      endDate: DateTimeOperations.internalToApiDate(rent.endDate),
      rent: rent.rent,
      landlord: {
        bankAccount: rent.landlord?.bankAccount,
      },
    };
  }

  private static convertAPIRentalCalculationToInternal(rentalCalculation: any): RentalCalculation {
    if (rentalCalculation) {
      return {
        additionalCost: rentalCalculation.additionalCost,
        additionalCostTypes: rentalCalculation.additionalCostTypes,
        totalRent: rentalCalculation.totalRent,
        utilityCost: rentalCalculation.utilityCost,
        utilityCostBilling: rentalCalculation.utilityCostBilling,
        utilityCostBillingDateFrom: rentalCalculation.utilityCostBillingDateFrom,
        utilityCostBillingDateTo: rentalCalculation.utilityCostBillingDateTo,
        utilityItems: rentalCalculation.utilityItems,
        heatingCost: rentalCalculation.heatingCost,
        heatingCostBilling: rentalCalculation.heatingCostBilling,
        heatingCostBillingDateFrom: rentalCalculation.heatingCostBillingDateFrom,
        heatingCostBillingDateTo: rentalCalculation.heatingCostBillingDateTo,
      };
    }
    return {
      additionalCost: 'combined',
      additionalCostTypes: false,
      utilityCostBilling: 'calendarYear',
      utilityItems: [],
      heatingCostBilling: 'calendarYear',
    };
  }

  private static convertAPIRentalAdjustmentToInternal(rentalAdjustment: any): RentalAdjustmentInterface {
    if (rentalAdjustment) {
      return {
        rentalAdjustment: rentalAdjustment.rentalAdjustment,
        rentRegulation: rentalAdjustment.rentRegulation,
        tiers: this.convertAPITiersToInternal(rentalAdjustment.tiers),
        firstUsedAndRented: rentalAdjustment.firstUsedAndRented,
        renovationsLastThreeYears: rentalAdjustment.renovationsLastThreeYears,
        occupancyAfterRenovations: rentalAdjustment.occupancyAfterRenovations,
      };
    }
    return {
      rentalAdjustment: RentalAdjustmentTypeEnum.COMPARABLE_RENT,
      rentRegulation: false,
      tiers: [],
      firstUsedAndRented: false,
      renovationsLastThreeYears: false,
      occupancyAfterRenovations: false,
    };
  }

  private static convertAPIPreviousToInternal(previousTenant: any): undefined | PreviousTenant {
    if (previousTenant) {
      const date: undefined | null | string = DateTimeOperations.apiDateToInternalDate(previousTenant.lastPaymentDate);
      return {
        available: previousTenant.available,
        lastPaymentDate: date,
        lastRent: previousTenant.lastRent,
      };
    }
    return {
      available: false,
    };
  }

  static convertAPIRentToInternal(rent: any): Rent {
    if (rent) {
      return {
        durationType: rent.durationType,
        startDate: DateTimeOperations.apiDateToInternalDate(rent.startDate),
        endDate: DateTimeOperations.apiDateToInternalDate(rent.endDate),
        limitedRentDurationType: rent.limitedRentDurationType,
        limitedRentReason: rent.limitedRentReason,
        bindingPeriod: rent.bindingPeriod,
        noticePeriod: rent.noticePeriod,
        rent: rent.rent,
        landlord: {
          bankAccount: rent.landlord?.bankAccount,
        },
      };
    }
    return {};
  }

  private static convertAPIDepositToInternal(deposit: any): Deposit {
    if (deposit) {
      return {
        ownDefinedAmount: deposit.ownDefinedAmount,
        ownDefined: deposit.ownDefined,
        months: deposit.months,
        depositSolutions: this.convertAPIDepositSolutionsToInternal(deposit.depositSolutions),
      };
    }
    return {
      ownDefinedAmount: 0,
      ownDefined: false,
      months: 3,
      depositSolutions: [],
    };
  }

  private static convertAPIDepositSolutionsToInternal(depositSolutions: any[]): DepositSolution[] {
    if (depositSolutions) {
      return depositSolutions.map(ct => {
        return {
          name: ct.name,
          label: ct.label,
          isPermitted: ct.isPermitted,
        };
      });
    }
    return [];
  }

  private static convertAPITiersToInternal(tiers: any[]): Tier[] {
    if (tiers) {
      return tiers.map(t => {
        return {
          date: DateTimeOperations.apiDateToInternalDate(t.date),
          rent: t.rent,
        };
      });
    }
    return [];
  }
}
